import { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from '../api';
import { safeLocal, safeSession } from '../utils/safeStorage';

const AuthContext = createContext(null);

// Storage helpers — localStorage = "mantener sesion iniciada"
// sessionStorage = solo durante la sesion del tab.
// safeLocal/safeSession: no crashean si el navegador bloquea el storage.
const readToken = () => safeLocal.getItem('token') || safeSession.getItem('token');
const clearToken = () => { safeLocal.removeItem('token'); safeSession.removeItem('token'); };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(readToken());
  const [loading, setLoading] = useState(true);
  // true cuando el cambio de `token` viene de la renovacion deslizante (evento
  // pop:token-renewed): mismo usuario, solo cambia el JWT -> NO refetch de /me.
  const renewedRef = useRef(false);

  useEffect(() => {
    const esRenovacion = renewedRef.current;
    renewedRef.current = false;
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      if (esRenovacion) return;
      api.get('/api/auth/me')
        .then(res => setUser(res.data))
        .catch(() => { setToken(null); clearToken(); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  // Sesion deslizante (2026-09-10): utils/session.js renueva el token en
  // segundo plano y lo guarda en storage; aqui solo sincronizamos el estado
  // (y por el effect de arriba, el header default de axios).
  useEffect(() => {
    const onRenewed = (e) => {
      const nuevo = e?.detail?.token;
      if (!nuevo) return;
      renewedRef.current = true;
      setToken(nuevo);
    };
    window.addEventListener('pop:token-renewed', onRenewed);
    return () => window.removeEventListener('pop:token-renewed', onRenewed);
  }, []);

  const login = async (email, password, keepSignedIn = true) => {
    const params = new URLSearchParams();
    params.append('username', email);
    params.append('password', password);
    const res = await api.post('/api/auth/login', params);
    // MFA: si el backend responde con mfa_required=true, devolvemos un objeto
    // especial; Login.js mostrara input de codigo y llamara a mfaChallenge().
    if (res.data?.mfa_required && res.data?.challenge_token) {
      return {
        mfaRequired: true,
        challengeToken: res.data.challenge_token,
        keepSignedIn,
      };
    }
    const { access_token, user: userData } = res.data;
    // Limpiar ambos antes de guardar en el correcto
    clearToken();
    if (keepSignedIn) {
      safeLocal.setItem('token', access_token);
    } else {
      safeSession.setItem('token', access_token);
    }
    api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const mfaChallenge = async (challengeToken, code, keepSignedIn = true) => {
    const res = await api.post('/api/auth/mfa/challenge', {
      challenge_token: challengeToken,
      code,
    });
    const { access_token, user: userData } = res.data;
    clearToken();
    if (keepSignedIn) {
      safeLocal.setItem('token', access_token);
    } else {
      safeSession.setItem('token', access_token);
    }
    api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    clearToken();
    delete api.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  // Re-fetch /me tras setup-profile (u otro cambio al propio user)
  const refreshUser = async () => {
    const res = await api.get('/api/auth/me');
    setUser(res.data);
    return res.data;
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, refreshUser, mfaChallenge }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
